import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Bot, Plane, Factory, Award, Trophy, Glasses, Box, Building2, GraduationCap, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const bgImages = [
  '/gallery/lab_13.jpg',
  '/gallery/lab_14.jpg',
  '/gallery/lab_15.jpg',
  '/gallery/lab_16.jpg',
  '/gallery/lab_17.jpg',
  '/gallery/lab_18.jpg',
];

const iconMap = {
  Bot,
  Plane,
  Factory,
  Award,
  Trophy,
  Glasses,
  Box,
  Building2,
  GraduationCap,
};

const Services = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentBgIndex, setCurrentBgIndex] = useState(0);

  useEffect(() => {
    fetchServices();
  }, []);

  // Auto-rotate background images
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentBgIndex((prev) => (prev + 1) % bgImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API_URL}/services?is_active=true`);
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="min-h-screen pt-32 pb-24">
      <SEO 
        title="Our Services - UAV Geo Solutions | Drone Labs, Robotics, DGCA RPTO"
        description="Explore our comprehensive services: Drone Lab Setup, Robotics & IoT Labs, DGCA RPTO Training, AR/VR Labs, 3D Printing Labs, Drone Soccer Arena, and more."
        keywords="drone lab services, robotics lab setup, IoT lab, DGCA RPTO setup, drone manufacturing, AR VR lab, 3D printing lab"
        url="/services"
      />
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h1 className="text-5xl md:text-7xl font-heading font-bold uppercase tracking-tighter mb-6 text-glow">
            Our Services
          </h1>
          <p className="text-base md:text-lg text-gray-400 font-light leading-relaxed max-w-3xl mx-auto">
            Comprehensive technology solutions for modern educational institutions
          </p>
        </motion.div>

        <div className="space-y-12">
          {services.map((service, index) => {
            const Icon = iconMap[service.icon] || Box;
            return (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                data-testid={`service-detail-${index}`}
                className="glass rounded-3xl overflow-hidden relative min-h-[400px]"
              >
                {index === 0 && (
                  <div className="absolute inset-0">
                    {bgImages.map((img, bgIndex) => (
                      <motion.div
                        key={bgIndex}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: bgIndex === currentBgIndex ? 1 : 0 }}
                        transition={{ duration: 1.5 }}
                        className="absolute inset-0 bg-cover bg-center"
                        style={{ backgroundImage: `url(${img})` }}
                      />
                    ))}
                    <div className="absolute inset-0 bg-black/60" />
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-indigo-500/10" />
                  </div>
                )}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-8 md:p-12 relative z-20">
                  <div>
                    <div className="mb-6">
                      <Icon size={56} className="text-primary" strokeWidth={1.5} />
                    </div>
                    <h2 className="text-3xl md:text-4xl font-heading font-semibold mb-6">{service.title}</h2>
                    <p className="text-base text-gray-400 leading-relaxed mb-8">{service.description}</p>
                    <Link to="/contact">
                      <Button
                        data-testid={`request-proposal-${index}`}
                        className="bg-primary text-black hover:bg-primary-hover uppercase tracking-widest font-bold"
                      >
                        Request Proposal
                      </Button>
                    </Link>
                  </div>
                  <div>
                    <h3 className="text-xs font-bold tracking-[0.2em] uppercase text-cyan-400 mb-6">Key Features</h3>
                    <ul className="space-y-4">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start space-x-3">
                          <CheckCircle size={20} className="text-primary flex-shrink-0 mt-1" />
                          <span className="text-base text-gray-300">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Services;
